/**
 * A message field where a value is one object, not five characters of syntax.
 * Chips render the value's own words ("Unit name"); the stored string is
 * unchanged (`{{payload.unit.name}}`), so nothing downstream knows the
 * difference.
 *
 * The element is contenteditable and deliberately UNCONTROLLED after mount:
 * React re-rendering a contenteditable on every keystroke destroys the caret.
 * We paint from props only when the incoming value isn't what we last emitted.
 */
import { useEffect, useRef } from 'react';
import type { PickerVariable } from './definitionEdit.js';
import { parseTemplate, serializeNodes } from './tokenText.js';

const CHIP_KNOWN =
  'mx-0.5 inline-flex select-none items-center rounded border border-info-border bg-info-soft px-1.5 py-px align-baseline text-xs font-medium text-info';
const CHIP_STALE =
  'mx-0.5 inline-flex select-none items-center rounded border border-danger-border bg-danger-soft px-1.5 py-px align-baseline text-xs font-medium text-danger line-through';

/** Build the field's children from the stored string. */
const paint = (el: HTMLElement, value: string, vars: PickerVariable[]): void => {
  el.replaceChildren();
  for (const seg of parseTemplate(value, vars)) {
    if (seg.kind === 'text') {
      // Newlines are <br>: a contenteditable div won't render "\n" itself.
      const parts = seg.text.split('\n');
      parts.forEach((part, i) => {
        if (i > 0) el.append(document.createElement('br'));
        if (part !== '') el.append(document.createTextNode(part));
      });
      continue;
    }
    const chip = document.createElement('span');
    chip.dataset['path'] = seg.path;
    chip.contentEditable = 'false';
    chip.className = seg.known ? CHIP_KNOWN : CHIP_STALE;
    chip.textContent = seg.label;
    if (!seg.known) chip.title = `This value isn't available here any more (${seg.path}).`;
    el.append(chip);
  }
};

export interface TokenFieldHandle {
  /** Drop a value in at the caret — what the picker calls. */
  insert: (v: PickerVariable) => void;
}

export const TokenField = ({
  value,
  vars,
  onChange,
  multiline,
  label,
  handleRef,
}: {
  value: string;
  vars: PickerVariable[];
  onChange: (next: string) => void;
  multiline?: boolean;
  label: string;
  /** Filled with the insert handle so the picker beside it can reach in. */
  handleRef?: React.MutableRefObject<TokenFieldHandle | null>;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef(value);
  /** Where the caret was when the field last had it. The picker takes focus,
   * so by insert time the live selection is either gone or collapsed at the
   * start — appending is right when there was never a caret here. */
  const savedRange = useRef<Range | null>(null);

  const rememberCaret = (): void => {
    const el = ref.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer)) savedRange.current = range.cloneRange();
  };

  // Paint on mount, and whenever the value changed somewhere OTHER than here
  // (a template loading, an undo elsewhere) — never on our own keystrokes.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (value === lastEmitted.current && el.childNodes.length > 0) return;
    lastEmitted.current = value;
    paint(el, value, vars);
  }, [value, vars]);

  const emit = (): void => {
    const el = ref.current;
    if (!el) return;
    const next = serializeNodes(el);
    lastEmitted.current = next;
    onChange(next);
  };

  if (handleRef) {
    handleRef.current = {
      insert: (v) => {
        const el = ref.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection();
        const chip = document.createElement('span');
        chip.dataset['path'] = v.path;
        chip.contentEditable = 'false';
        chip.className = CHIP_KNOWN;
        chip.textContent = v.label;
        // At the caret the field last had; appended when it never had one.
        const range = savedRange.current;
        if (range && el.contains(range.commonAncestorContainer)) {
          range.deleteContents();
          range.insertNode(chip);
          range.setStartAfter(chip);
          range.collapse(true);
          sel?.removeAllRanges();
          sel?.addRange(range);
        } else {
          el.append(chip);
        }
        emit();
      },
    };
  }

  return (
    <div
      ref={ref}
      role="textbox"
      aria-label={label}
      aria-multiline={multiline}
      contentEditable
      suppressContentEditableWarning
      onInput={() => {
        rememberCaret();
        emit();
      }}
      onKeyUp={rememberCaret}
      onMouseUp={rememberCaret}
      onBlur={emit}
      className={`min-w-0 flex-1 rounded border border-outline-strong bg-surface px-3 py-2 text-sm text-ink focus-visible:border-primary focus-visible:outline-none ${
        multiline ? 'min-h-[5.5rem] whitespace-pre-wrap' : 'whitespace-nowrap'
      }`}
    />
  );
};
